import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCourses, createCourse, updateCourse, deleteCourse, createReservation, createReservationBatch, updateReservation, deleteReservation, exportCourses, getBatchDateRange, updateCourseCategories as apiUpdateCourseCategories, getMonitorStats } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Plus, Minus, Edit2, Trash2, ChevronDown, GraduationCap, Download, UserPlus, CheckCircle, Search, CalendarDays, Bell, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { createPDF, addTable } from '../lib/pdf';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
import PaymentSplitField from '../components/PaymentSplitField';

const invalidSplit = (splits) => splits && splits.some(s => !s.method || !(parseFloat(s.amount) > 0));

function splitLabel(splits, bizumRecipients) {
  const nameFor = (method) => {
    if (method === 'cash') return 'Efectivo';
    if (method === 'caixa') return 'Caixa';
    if (method?.startsWith('bizum_')) {
      const r = (bizumRecipients || []).find(x => `bizum_${x.id}` === method);
      return r ? `Bizum ${r.name}` : 'Bizum';
    }
    return method;
  };
  return splits.map(s => `${nameFor(s.method)} (${(s.amount || 0).toFixed(2)}€)`).join(' / ');
}

const LOCATIONS = [
  { value: 'san_antolin', label: 'San Antolín' },
  { value: 'san_martin', label: 'San Martín' },
  { value: 'ria_niembro', label: 'Ría de Niembro' },
];

const todayStr = new Date().toISOString().split('T')[0];
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d; };
const emptyCourseForm = { date: new Date().toISOString().split('T')[0], time: '10:00', location: 'san_antolin', monitors: [{ name: '', cost: '' }], course_category: 'surf', is_cancelled: false, cancel_reason: '' };

const CAT_COLORS = {
  surf: { border: '#C2DCC8', bg: '#F5FAF6', dot: '#81B29A', badgeBg: 'rgba(194,220,200,0.35)', badgeText: '#3A7A5A' },
  skate: { border: '#F4A0B5', bg: '#FFF0F5', dot: '#E07A9F', badgeBg: 'rgba(244,160,181,0.35)', badgeText: '#C0547A' },
};
const CUSTOM_PALETTE = [
  { border: '#B5C8F4', bg: '#F0F5FF', dot: '#5B82E0', badgeBg: 'rgba(91,130,224,0.18)', badgeText: '#2A5AB4' },
  { border: '#F4D1A0', bg: '#FFF8EE', dot: '#E09A3A', badgeBg: 'rgba(224,154,58,0.18)', badgeText: '#A06010' },
  { border: '#D4B5F4', bg: '#F8F0FF', dot: '#9A5BE0', badgeBg: 'rgba(154,91,224,0.18)', badgeText: '#6A2AB4' },
  { border: '#B5EBF4', bg: '#F0FBFF', dot: '#3AB4D4', badgeBg: 'rgba(58,180,212,0.18)', badgeText: '#0A7494' },
  { border: '#F4C8B5', bg: '#FFF4F0', dot: '#D47050', badgeBg: 'rgba(212,112,80,0.18)', badgeText: '#A03020' },
  { border: '#B5F4D1', bg: '#F0FFF7', dot: '#3AD47A', badgeBg: 'rgba(58,212,122,0.18)', badgeText: '#0A7440' },
  { border: '#F4EBB5', bg: '#FFFBF0', dot: '#D4B83A', badgeBg: 'rgba(212,184,58,0.18)', badgeText: '#946800' },
];
function getCatColors(cat, allCategories) {
  if (!cat || cat === 'surf') return CAT_COLORS.surf;
  if (cat === 'skate') return CAT_COLORS.skate;
  const customCats = (allCategories || []).filter(c => c !== 'surf' && c !== 'skate');
  const idx = customCats.indexOf(cat);
  return CUSTOM_PALETTE[idx >= 0 ? idx % CUSTOM_PALETTE.length : 0];
}
function getCourseCardStyle(course, allCategories) {
  if (course.is_cancelled) return { border: '#E8A5A0', bg: '#FDF2F1', dot: '#D9534F', badgeBg: 'rgba(217,83,79,0.15)', badgeText: '#B33A32' };
  const hasMonitor = course.monitors?.some(m => m.name?.trim());
  if (!hasMonitor) return { border: '#F2CC8F', bg: '#FFF9F0', dot: '#F2CC8F' };
  const cat = course.course_category || (course.is_skate ? 'skate' : 'surf');
  return getCatColors(cat, allCategories);
}
const emptyResForm = { course_id: '', reservation_name: '', phone: '', email: '', dni: '', students: [{ name: '' }], num_students: 0, income_total: '', amount_paid: '', payment_method: 'cash', payment_splits: null, notes: '', multi_day: false, date_from: '', date_to: '', location: 'san_antolin', batch_id: '', selected_dates: [] };

export default function CoursesPage() {
  const { user, courseCategories, updateCourseCategories, bizumRecipients } = useAuth();
  const isCaseta = user?.role === 'caseta';
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('proximos');
  const [courseDialog, setCourseDialog] = useState(false);
  const [resDialog, setResDialog] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editingResId, setEditingResId] = useState(null);
  const [courseForm, setCourseForm] = useState({...emptyCourseForm});
  const [resForm, setResForm] = useState({...emptyResForm});
  const [openDates, setOpenDates] = useState({});
  const [openCourses, setOpenCourses] = useState({});
  const [search, setSearch] = useState('');
  const [calendarDate, setCalendarDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const isInitialLoad = useRef(true);
  const [batchSelectDialog, setBatchSelectDialog] = useState(false);
  const [pendingBatchPayload, setPendingBatchPayload] = useState(null);
  const [notifyingCourses, setNotifyingCourses] = useState(new Set());
  const [pendingBatchAmbiguousDays, setPendingBatchAmbiguousDays] = useState([]);
  const [batchDaySelections, setBatchDaySelections] = useState({});
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [savedMonitorNames, setSavedMonitorNames] = useState([]);
  const [monitorFocusIdx, setMonitorFocusIdx] = useState(null);

  useEffect(() => {
    getMonitorStats().then(res => {
      const names = (res.data || []).map(m => m.name).filter(Boolean).sort((a, b) => a.localeCompare(b));
      setSavedMonitorNames(names);
    }).catch(() => {});
  }, []);

  const getMonitorSuggestions = (val) => {
    if (!val || !val.trim()) return [];
    const lower = val.toLowerCase();
    return savedMonitorNames.filter(n => n.toLowerCase().includes(lower));
  };

  const defaultMonitorCost = (name) => {
    const lower = name.toLowerCase();
    return (lower.includes('botas') || lower === 'bada') ? 35 : 30;
  };

  const fetchData = useCallback(async () => {
    if (isInitialLoad.current) setLoading(true);
    try {
      const params = {};
      if (isCaseta) {
        const today = new Date();
        const twoDaysAgo = new Date(today);
        twoDaysAgo.setDate(today.getDate() - 2);
        params.date_from = twoDaysAgo.toISOString().split('T')[0];
      }
      const { data } = await getCourses(params); setCourses(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); isInitialLoad.current = false; }
  }, [isCaseta]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Course CRUD
  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...courseForm, monitors: courseForm.monitors.filter(m => m.name.trim()).map(m => ({...m, cost: parseFloat(m.cost) || 0})) };
      if (editingCourseId) {
        await updateCourse(editingCourseId, payload);
        toast.success('Curso actualizado');
        setCourses(prev => prev.map(c => c.id === editingCourseId ? { ...c, ...payload } : c));
      } else {
        await createCourse(payload);
        toast.success('Curso creado');
      }
      setCourseDialog(false); setEditingCourseId(null); setCourseForm({...emptyCourseForm}); fetchData();
    } catch { toast.error('Error'); }
  };

  const handleEditCourse = (c) => {
    setEditingCourseId(c.id);
    const cat = c.course_category || (c.is_skate ? 'skate' : 'surf');
    setCourseForm({ date: c.date, time: c.time, location: c.location, monitors: c.monitors?.length ? c.monitors.map(m => ({...m, cost: m.cost === 0 ? '' : m.cost})) : [{ name: '', cost: '' }], course_category: cat, is_cancelled: c.is_cancelled || false, cancel_reason: c.cancel_reason || '' });
    setCourseDialog(true);
  };

  const confirmNewCategory = async () => {
    const trimmed = newCategoryInput.trim().toLowerCase();
    if (!trimmed || courseCategories.includes(trimmed)) { setAddingCategory(false); setNewCategoryInput(''); return; }
    const updated = [...courseCategories, trimmed];
    try {
      await apiUpdateCourseCategories(updated);
      updateCourseCategories(updated);
      setCourseForm(f => ({ ...f, course_category: trimmed }));
    } catch { toast.error('Error guardando categoría'); }
    setAddingCategory(false);
    setNewCategoryInput('');
  };

  const deleteLastCategory = async () => {
    const customCats = courseCategories.filter(c => c !== 'surf' && c !== 'skate');
    if (!customCats.length) return;
    const lastCat = customCats[customCats.length - 1];
    const updated = courseCategories.filter(c => c !== lastCat);
    try {
      await apiUpdateCourseCategories(updated);
      updateCourseCategories(updated);
      if (courseForm.course_category === lastCat) setCourseForm(f => ({ ...f, course_category: 'surf' }));
    } catch { toast.error('Error eliminando categoría'); }
  };

  const handleDeleteCourse = async (id) => { if (!window.confirm('¿Eliminar curso y todas sus reservas?')) return; try { await deleteCourse(id); toast.success('Eliminado'); fetchData(); } catch { toast.error('Error'); } };

  // Reservation CRUD
  const openNewReservation = (courseId, course) => {
    setEditingResId(null);
    setResForm({...emptyResForm, course_id: courseId, location: course?.location || 'san_antolin', date_from: course?.date || '', date_to: course?.date || ''});
    setResDialog(true);
  };

  const handleEditReservation = async (r, course) => {
    setEditingResId(r.id);
    let batchDateFrom = ''; let batchDateTo = ''; let batchSelectedDates = []; let isMultiDay = false;
    if (r.batch_id) {
      isMultiDay = true;
      try {
        const { data } = await getBatchDateRange(r.batch_id);
        batchDateFrom = data.date_from || '';
        batchDateTo = data.date_to || '';
        batchSelectedDates = data.dates || [];
      } catch (_) {}
    }
    if (!batchDateFrom) {
      batchDateFrom = course?.date || new Date().toISOString().split('T')[0];
      batchDateTo = batchDateFrom;
      batchSelectedDates = batchDateFrom ? [batchDateFrom] : [];
    }
    setResForm({
      course_id: r.course_id, reservation_name: r.reservation_name || '', phone: r.phone || '',
      email: r.email || '', dni: r.dni || '', students: r.students?.length ? r.students : [{ name: '' }],
      num_students: r.num_students || 0,
      income_total: r.income_total || '', amount_paid: r.amount_paid || '', notes: r.notes || '',
      payment_method: r.payment_method || 'cash', payment_splits: r.payment_splits || null,
      multi_day: isMultiDay, date_from: batchDateFrom, date_to: batchDateTo, location: course?.location || 'san_antolin', batch_id: r.batch_id || '', selected_dates: batchSelectedDates, _origAmountPending: r.amount_pending ?? null, _origAmountPaid: r.amount_paid || 0
    });
    setResDialog(true);
  };

  const handleResSubmit = async (e) => {
    e.preventDefault();
    if (invalidSplit(resForm.payment_splits)) { toast.error('Completa ambos métodos del pago dividido con un importe mayor que 0'); return; }
    try {
      const students = resForm.students.filter(s => s.name.trim());
      const numSt = parseInt(resForm.num_students) || 0;
      const batchPayload = {
        date_from: resForm.date_from, date_to: resForm.date_to,
        location: resForm.location,
        reservation_name: resForm.reservation_name, phone: resForm.phone,
        email: resForm.email, dni: resForm.dni, students,
        num_students: numSt,
        income_total: parseFloat(resForm.income_total) || 0,
        amount_paid: parseFloat(resForm.amount_paid) || 0,
        payment_method: resForm.payment_method,
        payment_splits: resForm.payment_splits,
        notes: resForm.notes
      };
      if (resForm.multi_day && !editingResId && resForm.date_from && resForm.date_to) {
        const fullPayload = { ...batchPayload, first_course_id: resForm.course_id, specific_dates: resForm.selected_dates?.length > 0 ? resForm.selected_dates : null };
        const allDates = resForm.selected_dates?.length > 0 ? resForm.selected_dates : getDatesInRange(resForm.date_from, resForm.date_to);
        const ambiguous = allDates
          .filter(d => d !== resForm.date_from)
          .map(d => ({ date: d, options: courses.filter(c => c.date === d && c.location === resForm.location) }))
          .filter(d => d.options.length > 1);
        if (ambiguous.length > 0) {
          setPendingBatchPayload(fullPayload);
          setPendingBatchAmbiguousDays(ambiguous);
          setBatchDaySelections({});
          setBatchSelectDialog(true);
          return;
        }
        await createReservationBatch(fullPayload);
        toast.success('Reservas creadas en rango de fechas');
      } else {
        const payload = { ...resForm, income_total: parseFloat(resForm.income_total) || 0, amount_paid: parseFloat(resForm.amount_paid) || 0, students, num_students: numSt };
        if (editingResId) {
          const extBatchId = resForm.batch_id || (Date.now().toString(36) + Math.random().toString(36).slice(2));
          await updateReservation(editingResId, {...payload, batch_id: extBatchId});
          if (resForm.multi_day && resForm.date_from && resForm.date_to) {
            const editBatch = {...batchPayload, amount_paid: 0, payment_splits: null, batch_id: extBatchId, specific_dates: resForm.selected_dates?.length > 0 ? resForm.selected_dates : null};
            const allDatesEdit = resForm.selected_dates?.length > 0 ? resForm.selected_dates : getDatesInRange(resForm.date_from, resForm.date_to);
            const ambiguousEdit = allDatesEdit
              .map(d => ({ date: d, options: courses.filter(c => c.date === d && c.location === resForm.location) }))
              .filter(d => d.options.length > 1);
            if (ambiguousEdit.length > 0) {
              setPendingBatchPayload(editBatch);
              setPendingBatchAmbiguousDays(ambiguousEdit);
              const preSel = {}; ambiguousEdit.forEach(({date}) => { const bc = courses.find(c => c.date === date && (c.reservations||[]).some(res => res.batch_id === extBatchId)); if (bc) preSel[date] = bc.id; });
              setBatchDaySelections(preSel);
              setBatchSelectDialog(true);
              setResDialog(false); setEditingResId(null); setResForm({...emptyResForm}); fetchData();
              return;
            }
            await createReservationBatch(editBatch);
            toast.success('Reserva actualizada y ampliada a más días');
          } else {
            toast.success('Reserva actualizada');
          }
        } else {
          await createReservation(payload);
          toast.success('Reserva creada');
        }
      }
      setResDialog(false); setEditingResId(null); setResForm({...emptyResForm}); fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const handleDeleteReservation = async (id) => { if (!window.confirm('¿Eliminar reserva?')) return; try { await deleteReservation(id); toast.success('Eliminada'); fetchData(); } catch { toast.error('Error'); } };

  const handleBatchWithSelections = async () => {
    const allSelected = pendingBatchAmbiguousDays.every(({date}) => batchDaySelections[date]);
    if (!allSelected) { toast.error('Elige un curso para cada día'); return; }
    try {
      await createReservationBatch({ ...pendingBatchPayload, course_ids_by_date: batchDaySelections });
      toast.success('Reservas creadas en rango de fechas');
      setBatchSelectDialog(false); setResDialog(false); setResForm({...emptyResForm}); fetchData();
    } catch { toast.error('Error'); }
  };

  // Monitor helpers
  const addMonitor = () => setCourseForm(f => ({...f, monitors: [...f.monitors, { name: '', cost: '' }]}));
  const updateMonitor = (i, field, val) => { const m = [...courseForm.monitors]; m[i] = {...m[i], [field]: field === 'cost' ? parseFloat(val) || 0 : val}; setCourseForm(f => ({...f, monitors: m})); };
  const removeMonitor = (i) => setCourseForm(f => ({...f, monitors: f.monitors.filter((_, j) => j !== i)}));

  // Student helpers
  const addStudent = () => setResForm(f => ({...f, students: [...f.students, { name: '' }]}));
  const updateStudent = (i, val) => { const s = [...resForm.students]; s[i] = { name: val }; const cnt = s.filter(st => st.name.trim()).length; setResForm(f => ({...f, students: s, num_students: cnt > 0 ? cnt : f.num_students})); };
  const removeStudent = (i) => setResForm(f => { const ns = f.students.filter((_, j) => j !== i); return {...f, students: ns, num_students: ns.filter(s => s.name.trim()).length || f.num_students}; });

  const handleExport = async () => { try { const { data } = await exportCourses(); const url = URL.createObjectURL(data); const a = document.createElement('a'); a.href = url; a.download = 'cursos_reservas.xlsx'; a.click(); } catch { toast.error('Error exportando'); } };
  const handleExportPDF = () => {
    const doc = createPDF(); doc.setFontSize(16); doc.text('Cursos y Reservas - SURF SKULL', 14, 18);
    const rows = [];
    courses.forEach(c => {
      const mons = c.monitors?.map(m => `${m.name} (${m.cost}€)`).join(', ') || '-';
      (c.reservations || []).forEach(r => {
        rows.push([fmtDate(c.date), c.time, getLoc(c.location), mons, r.reservation_name, `${(r.income_total||0).toFixed(2)} €`, `${(r.amount_paid||0).toFixed(2)} €`, `${(r.amount_pending||0).toFixed(2)} €`]);
      });
      if (!c.reservations?.length) rows.push([c.date, c.time, getLoc(c.location), mons, '-', '-', '-', '-']);
    });
    addTable(doc, { startY: 25, head: [['Fecha','Hora','Ubicación','Monitores','Reserva','Total','Pagado','Pendiente']], body: rows, theme: 'grid', headStyles: { fillColor: [74,109,124] }, styles: { fontSize: 8 } });
    doc.save('cursos_reservas.pdf');
  };

  const toggleDate = (d) => setOpenDates(p => ({...p, [d]: !p[d]}));
  const toggleCourse = (id) => setOpenCourses(p => ({...p, [id]: !p[id]}));

  const getLoc = (v) => LOCATIONS.find(l => l.value === v)?.label || v;
  const getDatesInRange = (from, to) => {
    const dates = [];
    let cur = new Date(from + 'T00:00:00Z');
    const end = new Date(to + 'T00:00:00Z');
    while (cur <= end) { dates.push(cur.toISOString().split('T')[0]); cur.setUTCDate(cur.getUTCDate() + 1); }
    return dates;
  };

  const filtered = courses.filter(c => {
    if (calendarDate) { const d = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth()+1).padStart(2,"0")}-${String(calendarDate.getDate()).padStart(2,"0")}`; if (c.date !== d) return false; }
    if (dateFrom && c.date < dateFrom) return false;
    if (dateTo && c.date > dateTo) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    const courseMatch = [c.date, c.time, c.location, getLoc(c.location), ...(c.monitors||[]).map(m => m.name), (c.monitors||[]).length > 0 ? 'monitores' : null].filter(Boolean).some(f => f.toLowerCase().includes(s));
    const resMatch = (c.reservations||[]).some(r => { const keys = [r.reservation_name, String(r.income_total??''), String(r.amount_paid??''), String(r.amount_pending??''), String(r.num_students??''), ...(r.students||[]).map(st => st.name), r.amount_pending > 0 ? 'pendiente' : null, (r.amount_paid > 0 && !r.amount_pending) ? 'pagado' : null, r.income_total > 0 ? 'total' : null].filter(Boolean); return keys.some(f => String(f).toLowerCase().includes(s)); });
    return courseMatch || resMatch;
  });

  const grouped = filtered.reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});
  const allDates = Object.keys(grouped);
  const futureDates = allDates.filter(d => d >= todayStr).sort((a, b) => a.localeCompare(b));
  const pastDates = allDates.filter(d => d < todayStr).sort((a, b) => b.localeCompare(a));
  const dates = tab === 'proximos' ? futureDates : pastDates;

  const handleFarewellCourse = async (course) => {
    if ((course.farewell_count || 0) > 0) {
      if (!window.confirm(`Ya se envió despedida ${course.farewell_count} vez. ¿Enviar de nuevo a todas las reservas?`)) return;
    }
    try {
      const r = await fetch(`/api/courses/${course.id}/farewell`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (d.ok) {
        const msg = d.sent > 0 ? `Despedida enviada a ${d.sent} reserva(s)` : d.skipped > 0 ? `Sin envíos: ${d.skipped} reserva(s) aún no han terminado su curso múltiple` : 'Sin reservas con contacto para notificar';
        toast.success(msg);
        fetchData();
      } else toast.error('Error enviando despedida');
    } catch { toast.error('Error de conexión'); }
  };

  const handleNotifyCourse = async (course) => {
    if (notifyingCourses.has(course.id)) return;
    if ((course.notification_count || 0) > 0) {
      if (!window.confirm(`Ya se envió ${course.notification_count} vez. ¿Enviar de nuevo a todas las reservas?`)) return;
    }
    setNotifyingCourses(prev => new Set(prev).add(course.id));
    try {
      const r = await fetch(`/api/courses/${course.id}/notify`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (d.ok) {
        toast.success(`Notificado: ${d.total} reserva${d.total !== 1 ? 's' : ''}`);
        setCourses(prev => prev.map(c => c.id === course.id ? { ...c, notification_count: d.notification_count } : c));
      } else toast.error('Error al enviar notificaciones');
    } catch (e) { toast.error('Error de conexión'); }
    finally { setNotifyingCourses(prev => { const s = new Set(prev); s.delete(course.id); return s; }); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="courses-page">
      <div className="sticky top-0 z-10 bg-[#FCFBF9] pt-4 md:pt-8 pb-3 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Cursos</h1>
            <p className="text-sm text-[#8A9BA8] mt-1">{futureDates.length} próximos · {pastDates.length} en historial</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild><Button variant="outline" size="icon" className={`border-[#E5E0D8] h-9 w-9 ${calendarDate ? 'bg-[#4A6D7C] text-white hover:bg-[#3A5663]' : 'text-[#4A5D6C]'}`} data-testid="courses-calendar-btn"><CalendarDays className="w-4 h-4"/></Button></PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={calendarDate} month={calendarMonth} onMonthChange={setCalendarMonth} onSelect={(d) => { setCalendarDate(d); if (d) setCalendarMonth(d); setCalendarOpen(false); }} /></PopoverContent>
            </Popover>
            {calendarDate && <Button variant="ghost" size="sm" className="h-7 text-xs text-[#8A9BA8]" onClick={() => { setCalendarDate(null); setCalendarMonth(new Date()); }}>✕ {calendarDate.toLocaleDateString('es-ES')}</Button>}
            <div className="flex items-center gap-1">
              <Input type="date" value={dateFrom} onChange={e => { const v = e.target.value; setDateFrom(v); if (!dateTo || dateTo < v) setDateTo(v); }} className="border-[#E5E0D8] h-9 text-sm w-32" />
              {dateFrom && <button onClick={() => setDateFrom('')} style={{border:'none',background:'none',cursor:'pointer',color:'#8A9BA8',fontSize:14,fontWeight:700,padding:'0 2px'}}>×</button>}
              <span className="text-[#8A9BA8] text-xs">—</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border-[#E5E0D8] h-9 text-sm w-32" />
              {dateTo && <button onClick={() => setDateTo('')} style={{border:'none',background:'none',cursor:'pointer',color:'#8A9BA8',fontSize:14,fontWeight:700,padding:'0 2px'}}>×</button>}
            </div>
                      <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#8A9BA8]"/><Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 border-[#E5E0D8] w-24 sm:w-24 sm:w-44 h-9" data-testid="courses-search"/></div>
            {!isCaseta && <Button variant="outline" onClick={handleExport} data-testid="export-courses-btn" className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 sm:mr-2"/><span className="hidden sm:inline">Excel</span></Button>}
            {!isCaseta && <Button variant="outline" onClick={handleExportPDF} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 sm:mr-2"/><span className="hidden sm:inline">PDF</span></Button>}
            <Button onClick={() => { setEditingCourseId(null); setCourseForm({...emptyCourseForm}); setCourseDialog(true); }} data-testid="add-course-button" className="bg-[#4A6D7C] hover:bg-[#3A5663] text-white"><Plus className="w-4 h-4 mr-2"/>Nuevo Curso</Button>
          </div>
        </div>
        <div className="flex gap-1 bg-[#F5F2ED] p-1 rounded-lg w-fit">
          <button onClick={() => setTab('proximos')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'proximos' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
            Próximos Cursos{' '}
            <span className="ml-1 text-xs bg-[#4A6D7C] text-white rounded-full px-2 py-0.5">{futureDates.length}</span>
          </button>
          <button onClick={() => setTab('historial')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'historial' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
            Historial{' '}
            <span className="ml-1 text-xs bg-[#8A9BA8] text-white rounded-full px-2 py-0.5">{pastDates.length}</span>
          </button>
        </div>
      </div>

            {loading ? <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="skeleton-pulse h-14 rounded-lg"/>)}</div> : dates.length === 0 ? (
        <div className="text-center py-16 text-[#8A9BA8]"><GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-40"/>{tab === 'proximos' ? 'No hay próximos cursos' : 'No hay cursos en el historial'}</div>
      ) : dates.map(date => (
        <Collapsible key={date} open={openDates[date] !== false} onOpenChange={() => toggleDate(date)}>
          <CollapsibleTrigger className="w-full flex items-center justify-between p-4 bg-[#F5F2ED] rounded-lg hover:bg-[#EBE7E0]" data-testid={`course-date-${date}`}>
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-[#1A2B3C] tabular-nums">{fmtDate(date)}</span>
              <span className="text-xs text-[#8A9BA8]">{grouped[date].length} cursos</span>
            </div>
            <div className="flex items-center gap-3">
              {(() => {
                const dtIncome = grouped[date].reduce((s, c) => s + (c.reservations?.reduce((rs, r) => rs + (r.income_total || 0), 0) || 0), 0);
                const dtPaid = grouped[date].reduce((s, c) => s + (c.reservations?.reduce((rs, r) => rs + (r.amount_paid || 0), 0) || 0), 0);
                const dtPending = grouped[date].reduce((s, c) => s + (c.reservations?.reduce((rs, r) => rs + (r.amount_pending || 0), 0) || 0), 0);
                return (dtIncome > 0 || dtPaid > 0) ? (
                  <div className="text-right text-xs space-y-0.5 mr-1">
                    {dtIncome > 0 && <div className="text-[#4A6D7C] font-semibold tabular-nums">Total: {dtIncome.toFixed(2)} €</div>}
                    {dtPaid > 0 && <div className="text-[#81B29A] font-semibold tabular-nums">Cobrado: {dtPaid.toFixed(2)} €</div>}
                    {dtPending > 0 && <div className="text-[#E07A5F] tabular-nums">Pend: {dtPending.toFixed(2)} €</div>}
                  </div>
                ) : null;
              })()}
              <ChevronDown className="w-4 h-4 text-[#8A9BA8]" />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-3 pl-2">
            {grouped[date].map((course) => {
              const resCount = course.reservations?.length || 0;
              const totalIncome = course.reservations?.reduce((s, r) => s + (r.income_total || 0), 0) || 0;
              const totalPaid = course.reservations?.reduce((s, r) => s + (r.amount_paid || 0), 0) || 0;
              const totalPending = course.reservations?.reduce((s, r) => s + (r.amount_pending || 0), 0) || 0;
              const monitorNames = course.monitors?.map(m => m.name).filter(Boolean).join(', ') || '-';
              const confirmedMonitors = course.confirmed_monitors || [];
              const cs = getCourseCardStyle(course, courseCategories);
              const courseCat = course.course_category || (course.is_skate ? 'skate' : 'surf');
              return (
                <Collapsible key={course.id} open={openCourses[course.id] !== false} onOpenChange={() => toggleCourse(course.id)}>
                  <CollapsibleTrigger className="w-full" data-testid={`course-${course.id}`}>
                    <div style={{ border: `1.5px solid ${cs.border}`, background: cs.bg }} className="rounded-lg p-4 transition-colors hover:opacity-90">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-wrap flex-1">
                          <span style={{ background: cs.dot }} className="w-2.5 h-2.5 rounded-full flex-shrink-0" title={!course.monitors?.some(m => m.name?.trim()) ? 'Sin monitor asignado' : 'Curso con monitor'} />
                          <span className="text-sm font-bold text-[#1A2B3C] tabular-nums">{course.time}</span>
                          <span className="text-xs text-[#8A9BA8]">{getLoc(course.location)}</span>
                          <span className="text-xs text-[#4A5D6C]">
                            Monitores: {course.monitors?.map((m, mi) => (
                              <span key={m.name} className="inline-flex items-center gap-0.5">
                                {mi > 0 && <span className="text-[#8A9BA8] mx-0.5">/</span>}
                                {m.name}
                                {confirmedMonitors.includes(m.name) && <CheckCircle className="w-3 h-3 text-[#81B29A] inline" />}
                              </span>
                            ))}
                          </span>
                          <span className="text-xs bg-[#4A6D7C]/10 text-[#4A6D7C] px-2 py-0.5 rounded-full">{resCount} reservas</span>
                          <div className="relative inline-flex" onClick={e => e.stopPropagation()}>
                            <button onClick={() => handleFarewellCourse(course)} title="Enviar despedida a clientes" className={`text-base leading-none px-1 py-0.5 rounded transition-colors ${(course.farewell_count||0)>0?'text-red-500':'text-[#8A9BA8] hover:text-[#4A6D7C]'}`}>👋</button>
                            {(course.farewell_count||0)>0&&<span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center pointer-events-none leading-none">{course.farewell_count}</span>}
                          </div>
                          {courseCat !== 'surf' && !course.is_cancelled && <span style={{ background: cs.badgeBg, color: cs.badgeText }} className="text-xs px-2 py-0.5 rounded-full font-semibold uppercase">{courseCat}</span>}
                          {course.is_cancelled && <span style={{ background: cs.badgeBg, color: cs.badgeText }} className="text-xs px-2 py-0.5 rounded-full font-semibold uppercase">Cancelado</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right text-xs space-y-0.5 mr-2">
                            <div className="text-[#81B29A] font-semibold tabular-nums">{totalPaid.toFixed(2)} €</div>
                            {totalPending > 0 && <div className="text-[#E07A5F] tabular-nums">Pend: {totalPending.toFixed(2)} €</div>}
                          </div>
                          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                            <div className="relative">
                              <Button variant="ghost" size="icon" onClick={() => handleNotifyCourse(course)} disabled={notifyingCourses.has(course.id)} className={`h-7 w-7 ${notifyingCourses.has(course.id) ? 'opacity-40 text-[#E2B47D]' : (course.notification_count||0)>0 ? 'text-red-500' : 'text-[#E2B47D]'}`} title="Notificar reservas por WhatsApp/email"><Bell className="w-3.5 h-3.5"/></Button>
                              {(course.notification_count||0)>0&&<span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center pointer-events-none leading-none">{course.notification_count}</span>}
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => openNewReservation(course.id, course)} className="h-7 w-7 text-[#81B29A]" title="Añadir reserva"><UserPlus className="w-3.5 h-3.5"/></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleEditCourse(course)} className="h-7 w-7 text-[#4A6D7C]"><Edit2 className="w-3 h-3"/></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteCourse(course.id)} className="h-7 w-7 text-[#E07A5F]"><Trash2 className="w-3 h-3"/></Button>
                          </div>
                          <ChevronDown className="w-4 h-4 text-[#8A9BA8]"/>
                        </div>
                      </div>
                      {course.is_cancelled && course.cancel_reason && <div className="mt-2 text-xs text-[#B33A32] italic">Motivo: {course.cancel_reason}</div>}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-1 space-y-1 pl-4">
                    {resCount === 0 ? (
                      <p className="text-sm text-[#8A9BA8] p-2 italic">Sin reservas en este curso</p>
                    ) : course.reservations.map((r, ri) => (
                      <div key={r.id} className="flex items-center justify-between p-3 bg-white border border-[#E5E0D8] rounded-lg" data-testid={`reservation-${ri}`}>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-sm font-semibold text-[#1A2B3C]">{r.reservation_name || 'Sin nombre'}</span>
                            {r.confirmed && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-0.5"><CheckCircle className="w-3 h-3"/>Confirmado</span>}
                            {r.phone && <span className="text-xs text-[#8A9BA8]">{r.phone}</span>}
                            {r.dni && <span className="text-xs text-[#8A9BA8]">DNI: {r.dni}</span>}
                            {r.students?.length > 0 && <span className="text-xs text-[#4A5D6C]">{r.students.length} alumnos</span>}
                            {r.num_students > 0 && <span className="text-xs text-[#8A9BA8]">(Total: {r.num_students})</span>}
                            {r.farewell_sent && <span className="text-[11px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" title="Despedida enviada">👋 enviada</span>}
                          </div>
                          {r.notes && <div className="text-xs text-[#6A7B8A] italic mt-0.5">📝 {r.notes}</div>}
                          <div className="flex gap-4 text-xs tabular-nums">
                            <span className="text-[#81B29A] font-semibold">Total: {(r.income_total || 0).toFixed(2)} €</span>
                            {r.amount_paid > 0 && <span className="text-[#4A6D7C]">Pagado: {r.amount_paid.toFixed(2)} €</span>}
                            {r.amount_pending > 0 && <span className="text-[#E07A5F] font-semibold">Pendiente: {r.amount_pending.toFixed(2)} €</span>}
                            {r.payment_splits?.length > 1 && <span className="text-[#8A9BA8]">{splitLabel(r.payment_splits, bizumRecipients)}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEditReservation(r, course)} className="h-7 w-7 text-[#4A6D7C]"><Edit2 className="w-3 h-3"/></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteReservation(r.id)} className="h-7 w-7 text-[#E07A5F]"><Trash2 className="w-3 h-3"/></Button>
                        </div>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      ))}

      {/* Course Dialog */}
      <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
        <DialogContent className="bg-[#FCFBF9] border-[#E5E0D8] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>{editingCourseId ? 'Editar' : 'Nuevo'} Curso</DialogTitle></DialogHeader>
          <form onSubmit={handleCourseSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Fecha</Label><Input type="date" value={courseForm.date} onChange={e => setCourseForm({...courseForm, date: e.target.value})} required className="border-[#E5E0D8]"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Hora</Label><Input type="time" value={courseForm.time} onChange={e => setCourseForm({...courseForm, time: e.target.value})} required className="border-[#E5E0D8]"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Ubicación</Label>
                <Select value={courseForm.location} onValueChange={v => setCourseForm({...courseForm, location: v})}><SelectTrigger className="border-[#E5E0D8]"><SelectValue/></SelectTrigger><SelectContent>{LOCATIONS.map(l=><SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Monitores</Label><Button type="button" variant="ghost" size="sm" onClick={addMonitor} className="text-xs text-[#4A6D7C]"><Plus className="w-3 h-3 mr-1"/>Añadir</Button></div>
              {courseForm.monitors.map((m, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <div className="relative flex-1">
                    <Input placeholder="Nombre monitor" value={m.name} onChange={e => updateMonitor(i, 'name', e.target.value)} onFocus={() => setMonitorFocusIdx(i)} onBlur={() => setTimeout(() => setMonitorFocusIdx(null), 150)} className="border-[#E5E0D8] w-full" />
                    {monitorFocusIdx === i && getMonitorSuggestions(m.name).length > 0 && (
                      <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#E5E0D8] rounded shadow-md max-h-48 overflow-y-auto">
                        {getMonitorSuggestions(m.name).map(name => (
                          <li key={name} onMouseDown={() => { const monitors = [...courseForm.monitors]; monitors[i] = {...monitors[i], name, cost: monitors[i].cost || defaultMonitorCost(name)}; setCourseForm(f => ({...f, monitors})); setMonitorFocusIdx(null); }} className="px-3 py-2 cursor-pointer hover:bg-[#F5F0E8] text-sm">
                            {name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <Input placeholder="Coste €" type="number" step="0.01" value={m.cost} onChange={e => updateMonitor(i, 'cost', e.target.value)} className="border-[#E5E0D8] w-24" />
                  {courseForm.monitors.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeMonitor(i)} className="h-8 w-8 text-[#E07A5F]"><Trash2 className="w-3 h-3"/></Button>}
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Categoría</Label>
                {courseCategories.filter(c => c !== 'surf' && c !== 'skate').length > 0 && (
                  <Button type="button" variant="ghost" size="sm" onClick={deleteLastCategory} className="text-xs text-[#4A6D7C]">
                    <Minus className="w-3 h-3 mr-1"/>Eliminar
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {courseCategories.map(cat => {
                  const colors = getCatColors(cat, courseCategories);
                  const selected = courseForm.course_category === cat;
                  return (
                    <button key={cat} type="button" onClick={() => setCourseForm(f => ({ ...f, course_category: cat }))}
                      style={selected ? { background: colors.bg, border: `2px solid ${colors.border}`, color: colors.badgeText } : {}}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors capitalize ${selected ? '' : 'border-[#E5E0D8] text-[#8A9BA8] hover:border-[#4A6D7C]'}`}>
                      {cat}
                    </button>
                  );
                })}
                {addingCategory ? (
                  <input autoFocus value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)}
                    onBlur={confirmNewCategory}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmNewCategory(); } if (e.key === 'Escape') { setAddingCategory(false); setNewCategoryInput(''); } }}
                    className="px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-[#4A6D7C] outline-none w-28" placeholder="nombre..." maxLength={20}/>
                ) : (
                  <button type="button" onClick={() => setAddingCategory(true)}
                    className="px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-[#4A6D7C] text-[#4A6D7C] hover:bg-[#4A6D7C]/10 transition-colors">
                    + Nueva
                  </button>
                )}
              </div>
            </div>
            {editingCourseId && (
              !courseForm.is_cancelled ? (
                <Button type="button" variant="outline" onClick={() => setCourseForm(f => ({...f, is_cancelled: true}))}
                  className="w-full border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10">
                  <Ban className="w-4 h-4 mr-2"/>Cancelar curso
                </Button>
              ) : (
                <div className="space-y-2 rounded-lg border border-[#E07A5F]/40 bg-[#FDF2F1] p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B33A32]">Curso cancelado</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setCourseForm(f => ({...f, is_cancelled: false, cancel_reason: ''}))}
                      className="h-6 text-xs text-[#4A6D7C]">Reactivar curso</Button>
                  </div>
                  <Input placeholder="Motivo de la cancelación" value={courseForm.cancel_reason}
                    onChange={e => setCourseForm(f => ({...f, cancel_reason: e.target.value}))} className="border-[#E07A5F]/40"/>
                </div>
              )
            )}
            <Button type="submit" data-testid="course-submit-btn" className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">{editingCourseId ? 'Guardar' : 'Crear Curso'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reservation Dialog */}
      <Dialog open={resDialog} onOpenChange={setResDialog}>
        <DialogContent className="bg-[#FCFBF9] border-[#E5E0D8] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>{editingResId ? 'Editar' : 'Nueva'} Reserva</DialogTitle></DialogHeader>
          <form onSubmit={handleResSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={resForm.multi_day} onChange={e => { const chk = e.target.checked; setResForm(f => ({...f, multi_day: chk, selected_dates: chk && f.date_from && f.date_to ? getDatesInRange(f.date_from, f.date_to) : []})); }} className="rounded border-[#E5E0D8]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">{editingResId ? 'Ampliar reserva a más días' : 'Reserva varios días'}</span>
              </label>
              {resForm.multi_day && !editingResId && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Desde</Label><Input type="date" value={resForm.date_from} onChange={e => { const v = e.target.value; setResForm(f => { const t = (!f.date_to || f.date_to < v) ? v : f.date_to; return {...f, date_from: v, date_to: t, selected_dates: getDatesInRange(v, t)}; }); }} required className="border-[#E5E0D8]"/></div>
                  <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Hasta</Label><Input type="date" value={resForm.date_to} onChange={e => setResForm(f => ({...f, date_to: e.target.value, selected_dates: getDatesInRange(f.date_from, e.target.value)}))} required className="border-[#E5E0D8]"/></div>
                  <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Ubicacion</Label>
                    <Select value={resForm.location} onValueChange={v => setResForm({...resForm, location: v})}><SelectTrigger className="border-[#E5E0D8]"><SelectValue/></SelectTrigger><SelectContent>{LOCATIONS.map(l=><SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
              )}
              {resForm.multi_day && editingResId && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-[#F0F7F4] border border-[#81B29A]/30 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#81B29A]">Ampliar desde</Label>
                    <Input type="date" value={resForm.date_from} onChange={e => { const v = e.target.value; setResForm(f => { const t = (!f.date_to || f.date_to < v) ? v : f.date_to; return {...f, date_from: v, date_to: t, selected_dates: getDatesInRange(v, t)}; }); }} className="border-[#E5E0D8]"/>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#81B29A]">Ampliar hasta</Label>
                    <Input type="date" value={resForm.date_to} onChange={e => setResForm(f => ({...f, date_to: e.target.value, selected_dates: getDatesInRange(f.date_from, e.target.value)}))} className="border-[#E5E0D8]"/>
                  </div>
                </div>
              )}
              {resForm.multi_day && resForm.date_from && resForm.date_to && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Días ({(resForm.selected_dates||[]).length})</Label>
                    <div className="flex gap-2 text-xs">
                      <button type="button" onClick={() => setResForm(f => ({...f, selected_dates: getDatesInRange(f.date_from, f.date_to)}))} className="text-[#4A6D7C] hover:underline">Todos</button>
                      <span className="text-[#8A9BA8]">·</span>
                      <button type="button" onClick={() => setResForm(f => ({...f, selected_dates: []}))} className="text-[#4A6D7C] hover:underline">Ninguno</button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {getDatesInRange(resForm.date_from, resForm.date_to).map(d => { const dt = new Date(d + 'T12:00:00'); const wd = dt.toLocaleDateString('es-ES', {weekday:'short'}); const dd = dt.getDate(); const sel = (resForm.selected_dates||[]).includes(d); return <button key={d} type="button" onClick={() => setResForm(f => { const sd = f.selected_dates||[]; return {...f, selected_dates: sel ? sd.filter(x=>x!==d) : [...sd,d].sort()}; })} className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${sel ? 'bg-[#4A6D7C] text-white border-[#4A6D7C]' : 'bg-gray-100 text-gray-400 border-gray-200 line-through'}`}>{wd} {dd}</button>; })}
                  </div>
                </div>
              )}
            </div>
            <CustomerAutocomplete onSelect={c => c && setResForm(f => ({ ...f, reservation_name: c.name, phone: c.phone || f.phone, email: c.email || f.email, dni: c.dni || f.dni }))} className="mb-1" />
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nombre Reserva</Label><Input data-testid="res-name-input" value={resForm.reservation_name} onChange={e => setResForm({...resForm, reservation_name: e.target.value})} required className="border-[#E5E0D8]"/></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Teléfono</Label><Input value={resForm.phone} onChange={e => setResForm({...resForm, phone: e.target.value})} className="border-[#E5E0D8]"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Email</Label><Input value={resForm.email} onChange={e => setResForm({...resForm, email: e.target.value})} className="border-[#E5E0D8]"/></div>
            </div>
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">DNI</Label><Input value={resForm.dni} onChange={e => setResForm({...resForm, dni: e.target.value})} className="border-[#E5E0D8]"/></div>
            <div className="space-y-2">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">N Total Alumnos</Label><Input type="number" min="0" value={resForm.num_students} onChange={e => setResForm({...resForm, num_students: parseInt(e.target.value) || 0})} className="border-[#E5E0D8] w-32" placeholder="0"/></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Alumnos (nombres)</Label><Button type="button" variant="ghost" size="sm" onClick={addStudent} className="text-xs text-[#4A6D7C]"><Plus className="w-3 h-3 mr-1"/>Añadir</Button></div>
              {resForm.students.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input placeholder={`Alumno ${i+1}`} value={s.name} onChange={e => updateStudent(i, e.target.value)} className="border-[#E5E0D8] flex-1"/>
                  {resForm.students.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeStudent(i)} className="h-8 w-8 text-[#E07A5F]"><Trash2 className="w-3 h-3"/></Button>}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Total (€)</Label><Input data-testid="res-total-input" type="number" step="0.01" value={resForm.income_total} onChange={e => setResForm({...resForm, income_total: e.target.value})} className="border-[#E5E0D8]"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Pagado (€)</Label><Input data-testid="res-paid-input" type="number" step="0.01" value={resForm.amount_paid} disabled={resForm.payment_splits?.length > 1} onChange={e => setResForm({...resForm, amount_paid: e.target.value})} className="border-[#E5E0D8] disabled:opacity-60"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Forma de pago</Label>
                <Select value={resForm.payment_method} onValueChange={v => setResForm({...resForm, payment_method: v})} disabled={resForm.payment_splits?.length > 1}><SelectTrigger className="border-[#E5E0D8]"><SelectValue/></SelectTrigger><SelectContent>{(bizumRecipients||[]).map(r=><SelectItem key={r.id} value={`bizum_${r.id}`}>Bizum {r.name}</SelectItem>)}<SelectItem value="caixa">Caixa</SelectItem><SelectItem value="cash">Efectivo/Cash</SelectItem></SelectContent></Select>
              </div>
            </div>
            <PaymentSplitField
              paymentMethod={resForm.payment_method}
              paymentSplits={resForm.payment_splits}
              amountPaid={resForm.amount_paid}
              bizumRecipients={bizumRecipients}
              onChange={patch => setResForm(f => ({...f, ...patch}))}
            />
            {resForm.income_total && <p className="text-sm text-[#E07A5F] font-semibold tabular-nums">Pendiente: {(editingResId && resForm._origAmountPending != null ? Math.max(0, (resForm._origAmountPending||0) + (resForm._origAmountPaid||0) - (parseFloat(resForm.amount_paid)||0)) : Math.max(0, (parseFloat(resForm.income_total)||0) - (parseFloat(resForm.amount_paid)||0))).toFixed(2)} €</p>}
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Notas</Label><Input value={resForm.notes} onChange={e => setResForm({...resForm, notes: e.target.value})} className="border-[#E5E0D8]"/></div>
            <Button type="submit" data-testid="res-submit-btn" className="w-full bg-[#81B29A] hover:bg-[#6A9A83] text-white">{editingResId ? 'Guardar' : 'Crear Reserva'}</Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={batchSelectDialog} onOpenChange={setBatchSelectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A2B3C]" style={{fontFamily:'Manrope'}}>Elige el curso para cada día</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#8A9BA8]">Hay días con varios cursos disponibles. Elige en cuál añadir la reserva.</p>
            {pendingBatchAmbiguousDays.map(({date, options}) => (
              <div key={date} className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">{fmtDate(date)}</Label>
                <Select value={batchDaySelections[date] || ''} onValueChange={v => setBatchDaySelections(p => ({...p, [date]: v}))}>
                  <SelectTrigger className="border-[#E5E0D8]"><SelectValue placeholder="Elige curso..."/></SelectTrigger>
                  <SelectContent>
                    {options.map(c => <SelectItem key={c.id} value={c.id}>{c.time} — {getLoc(c.location)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <Button onClick={handleBatchWithSelections} className="w-full mt-4 bg-[#81B29A] hover:bg-[#6A9A83] text-white">Crear reservas</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
